/* Shipping/quantity tolerance — the ONE parse/format pair.
 *
 * The trade recap expresses a contractual tolerance as a lower and an upper
 * bound, each with its own unit, plus a combined display string
 * ("10% to 10%") kept for back-compat. Freight recaps now use the same shape
 * (task card: freight-recap-tolerance-parity), so the helpers live here
 * instead of being copy-pasted a fourth time — TradeRecap.js, DeliveryForm.js
 * and OfferGenerator.js each carry an inline twin of exactly this logic and
 * they are the reason the shapes had already started to drift.
 *
 * Extracted verbatim from TradeRecap.js (parseToleranceString, ~line 456) and
 * its ``formatToleranceField`` so the round-trip is bit-identical for rows
 * those screens wrote.
 */

const EMPTY = Object.freeze({
  lowerValue: '',
  lowerUnit: '%',
  upperValue: '',
  upperUnit: '%',
});

// Units offered by the paired control. Matches the trade recap's contractual
// vocabulary: a percentage of the base quantity, or an absolute tonnage.
const TOLERANCE_UNITS = ['%', 'MT'];

/** "10% to 12%" -> { lowerValue: '10', lowerUnit: '%', ... }. */
const parseToleranceString = (toleranceString) => {
  if (!toleranceString) return { ...EMPTY };

  const splitPart = (part) => {
    const match = (part || '').trim().match(/^(\d*\.?\d*)(.*)$/);
    if (!match) return null;
    return { value: match[1], unit: (match[2] || '').trim() || '%' };
  };

  if (toleranceString.includes('to')) {
    const [lower, upper] = toleranceString.split('to');
    const low = splitPart(lower);
    const up = splitPart(upper);
    return {
      lowerValue: low ? low.value : '',
      lowerUnit: low ? low.unit : '%',
      upperValue: up ? up.value : '',
      upperUnit: up ? up.unit : '%',
    };
  }

  // Single value — lands in the UPPER bound, matching OfferGenerator and the
  // trade recap. Changing that side would silently move existing data.
  const single = splitPart(toleranceString);
  if (!single) return { ...EMPTY };
  return {
    lowerValue: '',
    lowerUnit: '%',
    upperValue: single.value,
    upperUnit: single.unit,
  };
};

/** The inverse: the combined string the backend stores for display. */
const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
  const lower = lowerValue ? `${lowerValue}${lowerUnit || '%'}` : '';
  const upper = upperValue ? `${upperValue}${upperUnit || '%'}` : '';

  if (lower && upper) return `${lower} to ${upper}`;
  return lower || upper || '';
};

export { EMPTY as EMPTY_TOLERANCE, TOLERANCE_UNITS, parseToleranceString, formatToleranceField };
